import {
  Box,
  Divider,
  Grid2 as Grid,
  InputAdornment,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { DataGrid, TextField } from "../common";
import { GridColDef } from "@mui/x-data-grid";
import { formatDate, theme } from "../../utils";
import { useAtom } from "jotai";
import {
  multiplePaymentsDataGridPaginationAtom,
  toastOptionsAtom,
} from "../../atoms";
import { InvoiceItem, InvoiceItemForCheckPayment } from "../../types";
import { useEffect, useState } from "react";
import {
  Payment as PaymentIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { z } from "zod";
import { multipleCheckPaymentSchema } from "../../validations";
import { useMultipleCheckPaymentMutation } from "../../api";

export const MultipleCheckPayments = () => {
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const navigate = useNavigate();
  const { state } = useLocation();
  const selectedInvoices: InvoiceItem[] = state?.selectedInvoicesForPayment;
  const [rows, setRows] = useState<InvoiceItemForCheckPayment[]>(
    selectedInvoices
      .map((item) => ({
        ...item,
        amount: String(item.balanceAmount),
        bankName: "",
        checkNumber: "",
      }))
      .sort((a, b) => b.invoiceDate.localeCompare(a.invoiceDate))
      .sort((a, b) => a.accountName.localeCompare(b.accountName)) || [],
  );

  const [validationErrors, setValidationErrors] = useState<{
    checkNumber: { [rowId: number]: string };
    bankName: { [rowId: number]: string };
    amount: { [rowId: number]: string };
  }>();

  const [multiplePaymentsPaginationModel, setMultiplePaymentsPaginationModel] =
    useAtom(multiplePaymentsDataGridPaginationAtom);

  const multipleCheckPaymentMutation = useMultipleCheckPaymentMutation();

  const columns: GridColDef[] = [
    {
      field: "invoiceDate",
      headerName: "Date",
      width: 195,
      editable: false,
      valueGetter: (value) => {
        return formatDate(value, "dddd - MMMM DD,YYYY");
      },
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 195,
      editable: false,
      valueGetter: (value) => {
        return formatDate(value, "dddd - MMMM DD,YYYY");
      },
    },
    {
      field: "accountName",
      headerName: "Account Name",
      width: 150,
      editable: false,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      width: 120,
      editable: false,
    },
    {
      field: "invoiceAmount",
      headerName: "Invoice Amount",
      width: 120,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueFormatter: (value) => {
        return `$ ${parseFloat(value).toFixed(2)}`;
      },
    },
    {
      field: "paidAmount",
      headerName: "Paid Amount",
      width: 120,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueFormatter: (value) => {
        return `$ ${parseFloat(value).toFixed(2)}`;
      },
    },
    {
      field: "balanceAmount",
      headerName: "Balance Amount",
      width: 130,
      align: "center",
      headerAlign: "center",
      editable: false,
      valueFormatter: (value) => {
        return `$ ${parseFloat(value).toFixed(2)}`;
      },
    },
    {
      field: "checkNumber",
      headerName: "Check Number",
      width: 170,
      align: "center",
      headerAlign: "center",
      editable: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <TextField
            label={""}
            name={""}
            labelSx={{ marginBottom: "0px" }}
            defaultValue={params.row.checkNumber}
            size="small"
            error={
              validationErrors?.checkNumber[params.row.id]
                ? {
                    type: "manual",
                    message: validationErrors.checkNumber[params.row.id],
                  }
                : undefined
            }
            fullWidth
            sx={{ backgroundColor: "white" }}
          />
        </Box>
      ),
    },
    {
      field: "bankName",
      headerName: "Bank Name",
      width: 170,
      align: "center",
      headerAlign: "center",
      editable: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <TextField
            label={""}
            name={""}
            labelSx={{ marginBottom: "0px" }}
            defaultValue={params.row.bankName}
            size="small"
            error={
              validationErrors?.bankName[params.row.id]
                ? {
                    type: "manual",
                    message: validationErrors.bankName[params.row.id],
                  }
                : undefined
            }
            fullWidth
            sx={{ backgroundColor: "white" }}
          />
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 200,
      align: "center",
      headerAlign: "center",
      editable: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
            justifyContent: "center",
          }}
        >
          <TextField
            label={""}
            name={""}
            type="number"
            labelSx={{ marginBottom: "0px" }}
            valueAsNumber={true}
            defaultValue={params.row.amount}
            error={
              validationErrors?.amount[params.row.id]
                ? {
                    type: "manual",
                    message: validationErrors.amount[params.row.id],
                  }
                : undefined
            }
            size="small"
            fullWidth
            sx={{ backgroundColor: "white" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
          />
        </Box>
      ),
    },
  ];

  const handleProcessRowUpdate = (newRow: InvoiceItemForCheckPayment) => {
    const updatedRows = rows.map((row) =>
      row.id === newRow.id
        ? {
            ...row,
            ...newRow,
            checkNumber: newRow.checkNumber.trim(),
            bankName: newRow.bankName.trim(),
            amount: newRow.amount.trim(),
          }
        : row,
    );
    setRows(updatedRows);
    return newRow;
  };

  const checkValidation = (): boolean => {
    const newErrors: typeof validationErrors = {
      checkNumber: {},
      bankName: {},
      amount: {},
    };
    let isValid = true;
    rows.forEach((row) => {
      try {
        multipleCheckPaymentSchema
          .refine((data) => Number(data.amount) <= row.balanceAmount, {
            message: `Amount cannot exceed ${row.balanceAmount}`,
            path: ["amount"],
          })
          .refine(
            (data) => !isNaN(Number(data.amount)) && Number(data.amount) > 0,
            {
              message: "Enter a valid amount",
              path: ["amount"],
            },
          )
          .parse({
            checkNumber: row.checkNumber,
            bankName: row.bankName,
            amount: row.amount,
          });
      } catch (e) {
        isValid = false;
        if (e instanceof z.ZodError) {
          e.errors.forEach((error) => {
            if (error.path.includes("amount")) {
              newErrors.amount[row.id] = error.message;
            } else if (error.path.includes("bankName")) {
              newErrors.bankName[row.id] = error.message;
            } else if (error.path.includes("checkNumber")) {
              newErrors.checkNumber[row.id] = error.message;
            }
          });
        }
      }
    });
    setValidationErrors(newErrors);
    return isValid;
  };

  const handleProcessPayment = () => {
    if (checkValidation()) {
      multipleCheckPaymentMutation.mutate({
        orgId: selectedInvoices[0].orgId,
        items: rows.map((row) => ({
          accountId: row.accountId,
          invoiceNumber: row.invoiceNumber,
          checkNumber: row.checkNumber || "",
          bankName: row.bankName || "",
          amount: Number(row.amount),
        })),
      });
    }
  };

  const handleReturn = () => {
    navigate(-1);
  };

  const totalAmount = rows.reduce((sum, row) => {
    const amount = Number(row.amount);
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);

  useEffect(() => {
    validationErrors && checkValidation();
  }, [rows]);

  useEffect(() => {
    if (multipleCheckPaymentMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Payment processed successfully.",
        severity: "success",
      });
      handleReturn();
    }

    if (multipleCheckPaymentMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    multipleCheckPaymentMutation.isSuccess,
    multipleCheckPaymentMutation.isError,
    multipleCheckPaymentMutation.isLoading,
    multipleCheckPaymentMutation.data,
    multipleCheckPaymentMutation.error,
  ]);

  return (
    <Grid
      container
      direction={"column"}
      spacing={3}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "75vh",
        direction: "row",
        borderRadius: "10px",
        backgroundColor: "white",
        boxShadow: "0px 0px 4px 0px #00000040",
        p: 3,
        justifyContent: "flex-start",
        position: "relative",
      }}
    >
      <>
        <Grid container size={12} direction={"row"}>
          <Grid container size={{ sm: 6, xs: 12 }} direction={"row"}>
            <Grid
              size={12}
              gap={3}
              direction={"row"}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <KeyboardBackspaceIcon
                sx={{ transform: "translateY(-3px)", cursor: "pointer" }}
                onClick={handleReturn}
              />
              <Typography fontSize={"1.4rem"}>
                Multiple Payments - Check
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={12} direction={"row"}>
          <Divider sx={{ width: "100%" }} />
        </Grid>
        <DataGrid
          getRowHeight={(params) => {
            const rowId = Number(params.id);
            if (
              validationErrors?.checkNumber?.[rowId] ||
              validationErrors?.bankName?.[rowId] ||
              validationErrors?.amount?.[rowId]
            ) {
              return 80;
            }
            return 50;
          }}
          checkboxSelection={false}
          loading={multipleCheckPaymentMutation.isLoading}
          columns={columns}
          rows={rows}
          processRowUpdate={handleProcessRowUpdate}
          totalRecords={selectedInvoices.length}
          pageSize={10}
          paginationModel={multiplePaymentsPaginationModel}
          setPaginationModel={setMultiplePaymentsPaginationModel}
          sx={{
            backgroundColor: theme.palette.secondary.light,
            padding: "20px",
            paddingTop: "0px",
            "& .MuiSvgIcon-root": {
              fontSize: 16,
            },
            borderBottomLeftRadius: "10px",
            borderBottomRightRadius: "10px",
            height: "70vh",
          }}
          footerProps={{
            hasAddIcon: true,
            addBtnLabel: "Process Payment",
            disableAddBtn: multipleCheckPaymentMutation.isLoading,
            customAddIcon: PaymentIcon,
            onAddButtonClick: handleProcessPayment,
            removeBtnLabel: "Cancel",
            hasRemoveIcon: false,
            customModalIcon: (
              <CancelIcon
                fontSize="medium"
                sx={{ color: theme.palette.primary.main }}
              />
            ),
            totalAmount: totalAmount,
            handleConfirmRemove: handleReturn,
            confirmationModalTitle: "Cancel Payment",
            confirmationModalMessage: `Are you sure you want to cancel this amount?`,
          }}
        />
      </>
    </Grid>
  );
};
